// Misc
import { useTranslation } from "react-i18next";

// Redux
import { useAppSelector } from "../../../redux/hooks";

// Mui
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import UploadFileIcon from "../../../Assets/Images/Apps/CertificateValidator/upload-icon.svg";
import VerifiedIcon from "../../../Assets/Images/Apps/CertificateValidator/pdf-verified-icon.svg";
import FileDropzone from "../../Common/FileDropzone";
import { usePdfValidation } from "./hooks/usePdfValidation";

type Props = {
    signingCertificateName: string;
    environment: TEnv;
    api_identifier: string;
    brandName: TAccountType;
};

const UploadPDF = ({ signingCertificateName, environment, api_identifier, brandName }: Props) => {
    const { t } = useTranslation();

    const { validity } = useAppSelector((state) => ({
        validity: state.certificate_validator.pdfValidity,
    }));

    const { validatePdf, loading } = usePdfValidation(environment, api_identifier, brandName, signingCertificateName);

    const onFileUpload = async (files: File[]) => {
        const pdf = files[0];
        await validatePdf(pdf);
    };

    // If no key_name is configured, this means there is not pdf validation
    if (!signingCertificateName) return null;

    return (
        <FileDropzone
            onDrop={onFileUpload}
            loading={false}
            disabled={loading}
            accept={{ "application/pdf": [".pdf"] }}
            maxFiles={1}
            validity={validity}
            ariaLabel={t("certificate_validator.upload_pdf.section_label", "PDF certificate validation")}
            inputId="pdf-upload-input"
        >
            {loading ? (
                <Box role="status" aria-live="polite">
                    <Typography className="as-body-text" variant="body1" style={{ fontWeight: 600 }} component="h2">
                        {t("certificate_validator.upload_pdf.checking_validity", "Checking Validity...")}
                    </Typography>
                    <br />
                    <CircularProgress
                        aria-label={t(
                            "certificate_validator.upload_pdf.loading_label",
                            "Validating PDF certificate, please wait"
                        )}
                    />
                </Box>
            ) : (
                <>
                    {validity === "not_checked" && (
                        <div>
                            <Typography
                                variant="body1"
                                className="as-body-text"
                                style={{ fontWeight: 600 }}
                                component="h2"
                            >
                                {t("certificate_validator.upload_pdf.verify_certificate", "Validate PDF Certificate")}
                            </Typography>
                            <Typography variant="body2" id="pdf-upload-instructions">
                                {t(
                                    "certificate_validator.upload_pdf.upload_pdf",
                                    "Upload the PDF version of this certificate to validate its authenticity."
                                )}
                            </Typography>
                            <img
                                src={UploadFileIcon}
                                alt={t("certificate_validator.upload_pdf.upload_icon_alt", "Upload file icon")}
                                role="presentation"
                            />
                        </div>
                    )}
                    {validity === "valid" && (
                        <Box
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                            role="status"
                            aria-live="polite"
                        >
                            <img
                                src={VerifiedIcon}
                                alt={t(
                                    "certificate_validator.upload_pdf.verified_icon_alt",
                                    "Certificate verified checkmark icon"
                                )}
                            />
                            <Box style={{ width: "80%" }}>
                                <Typography
                                    variant="body1"
                                    className="as-body-text"
                                    style={{ fontWeight: 600 }}
                                    component="h2"
                                >
                                    {t(
                                        "certificate_validator.upload_pdf.successfully_verified",
                                        "PDF certificate was successfully validated"
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    {t(
                                        "certificate_validator.upload_pdf.matches_digital_version",
                                        "The PDF version uploaded matches the digital signature of this certificate."
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {["invalid", "invalid_id"].includes(validity) && (
                        <div role="alert" aria-live="assertive">
                            <Typography
                                variant="body1"
                                className="as-body-text"
                                style={{ fontWeight: 600 }}
                                component="h2"
                            >
                                {t(
                                    "certificate_validator.upload_pdf.certificate_not_valid",
                                    "PDF certificate is not valid"
                                )}
                            </Typography>
                            <Typography variant="body2">
                                {validity === "invalid_id"
                                    ? t(
                                          "certificate_validator.upload_pdf.invalid_id",
                                          "The uploaded PDF file does not match the certificate. Please upload another file."
                                      )
                                    : t(
                                          "certificate_validator.upload_pdf.does_not_matches_digital_version",
                                          "The PDF version uploaded does not match the digital signature of this certificate. Upload new version to validate again."
                                      )}
                            </Typography>
                            <img
                                src={UploadFileIcon}
                                alt={t("certificate_validator.upload_pdf.upload_icon_alt", "Upload file icon")}
                                role="presentation"
                            />
                        </div>
                    )}
                </>
            )}
        </FileDropzone>
    );
};

export default UploadPDF;
